import { Component, EventEmitter, Output } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { getUkPhoneValidator } from '../../../shared/validators/uk-phone.validator';
import { User, UserService } from '../../../root/services/user.service';
import { getExceptionMessage } from '../../../shared/functions/get-exception-message';
import { AuthService } from '../../../root/services/auth.service';
import { FormFieldService } from '../../../../../../../src/app/website/services/form-field.service';
import { of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'hfc-sign-up-mobile-authorisation',
  templateUrl: './sign-up-mobile-authorisation.component.html',
  styleUrls: ['./sign-up-mobile-authorisation.component.scss']
})
export class SignUpMobileAuthorisationComponent {
  @Output() complete = new EventEmitter<boolean>();

  public form = this.fb.group({
    prefix: ['+44', Validators.required],
    number: [null, [Validators.required, getUkPhoneValidator('prefix')]]
  });

  public isSubmitting = false;
  public error: string;
  public verifyMobileNumber = false;
  public isRequestNewCodeRed = false;
  public isRequestNewCodeLoading = false;
  public requestMessage: string | null = null;

  public remainingSeconds = 30;
  public canRequestNewCode = false;

  private lastSentAt = new Date();
  private countdownHandle: any;

  private now = new Date();

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private userService: UserService,
    private formFieldService: FormFieldService
  ) {}

  public get user() {
    return this.userService.user;
  }

  public get mobile() {
    return `${this.user.mobilePrefix} ${this.user.mobile}`;
  }

  get numberControl() {
    return this.form.get('number');
  }

  public onNextClicked(): void {
    if (!this.formFieldService.checkFormValidity(this.form)) {
      return;
    }

    this.isSubmitting = true;

    this.authService
      .setupMobileNumberObservable(this.form.value) // Form submission input
      .pipe(
        tap(() => {
          this.userService.user.mobile = this.form.value.number;
          this.userService.user.mobilePrefix = this.form.value.prefix;
        }),
        switchMap(() => {
          this.verifyMobileNumber = true;
          return of(true);
        }),
        catchError((error) => {
          this.error = error?.error?.message;
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe();
  }

  public onCodeEntered(code: string) {
    this.isSubmitting = true;
    this.error = ''; // Clear previous errors

    this.authService
      .verifySmsCodeObservable(code)
      .pipe(
        tap((userSession: User) => {
          // Success path
          this.userService.setUser(userSession);
          this.securityStepCompleted(); // emit success
        }),
        catchError((error) => {
          this.error = error?.error?.message ?? 'Invalid verification code. Please try again.';
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe();
  }

  public async onRequestNewClicked() {
    const now = new Date();
    const secondsSinceLast = (now.getTime() - this.lastSentAt.getTime()) / 1000;

    if (secondsSinceLast < 30) {
      this.isRequestNewCodeRed = true;
      this.requestMessage = 'Please wait 30 seconds before requesting another code';
      return;
    }

    if (!navigator.onLine) {
      this.isRequestNewCodeRed = true;
      this.requestMessage = 'No internet connection. Please check your network and try again.';
      return;
    }

    this.isRequestNewCodeRed = false;
    this.requestMessage = null;
    this.isRequestNewCodeLoading = true;

    this.authService
      .requestNewSmsCodeObservable()
      .pipe(
        catchError((error) => {
          // status 0
          if (error.status === 0 || !navigator.onLine) {
            this.error = 'An error occurred resending the code. Please try again.';
          } else {
            this.error = getExceptionMessage(error) || 'An error occurred resending the code. Please try again.';
          }

          this.isRequestNewCodeRed = true;
          this.requestMessage = this.error;

          return of(null);
        }),
        finalize(() => {
          this.isRequestNewCodeLoading = false;
        })
      )
      .subscribe(() => {
        this.requestMessage = 'A new code has been sent.';
        this.isRequestNewCodeRed = false;
        this.lastSentAt = new Date();
        this.startOrRefreshCountdown();
      });
  }

  private securityStepCompleted(): void {
    this.complete.emit(true);
  }

  private startOrRefreshCountdown() {
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle);
    }
    this.tickCountdown(); // compute immediately for UI
    this.countdownHandle = setInterval(() => this.tickCountdown(), 1000);
  }

  private tickCountdown() {
    const elapsed = (Date.now() - this.lastSentAt.getTime()) / 1000;
    const remaining = Math.max(0, Math.ceil(30 - elapsed));
    this.remainingSeconds = remaining;
    this.canRequestNewCode = remaining === 0;
  }
}
